<template>
  <img
    class="BaseImage observe-size"
    :alt="properties.alt || ''"
    v-bind="getWidthAndHeight(properties.src)"
    :src="
      $isServer
        ? getPlaceholderSource(properties.src)
        : resizedSrc || getPlaceholderSource(properties.src)
    "
    :data-src="properties.src || defaultImage"
    data-tag="BaseImage"
  />
</template>

<script>
/*
	For elements where the img size can change,
	make sure to pass the class: observe-size, to initiate a resize observer
	This will re-run the BaseImage.plugin when the element resizes.

	- todo 
	-- In the previewer lazy load the images
*/

import debounce from 'lodash/debounce';
const RESIZEOBSERVERTIMEOUT = 400;
const CLOUDINARY_BASE = `https://res.cloudinary.com/ditbeukyz/image/upload/`;

function getWidthHeight(src) {
  if (typeof src === 'string') {
    let width, height;

    const ratio = src.split('cratio=')[1];

    if (ratio) {
      [width, height] = ratio.split('x');
    }

    return {
      width,
      height,
    };
  }
}

const defaultImage =
  'https://res.cloudinary.com/ditbeukyz/image/upload/v1605878200/Resources/placeholder.svg?cratio=16x9';

function getPlaceholderSource(src) {
  if (!src) return defaultImage;

  const {width, height} = getWidthHeight(src);

  return `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}"%3E%3C/svg%3E`;
}

function getWidthAndHeight(src) {
  if (!src) return {width: 16, height: 9};

  const {width, height} = getWidthHeight(src);

  return {
    width,
    height,
  };
}

// Quick fix to limit file size in the previewer, if we are able to hydrate the baseImage, we might write
// the entire logic here
function getPreviewerCrop(src) {
  const width_to_use = 1200;
  return `${CLOUDINARY_BASE}w_${width_to_use},f_auto/${
    src.split(CLOUDINARY_BASE)[1]
  }`;
}

export default {
  name: 'BaseImage',

  props: {
    properties: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    defaultImage,
    observer: false,
    resizedSrc: false,
    maxSizeLoaded: 0,
    replaceImgSrc: () => {},
  }),
  watch: {
    'properties.src'() {
      this.observer.unobserve(this.$el);
      this.replaceImgSrc(undefined, true);
      window.setTimeout(() => {
        this.observer.observe(this.$el);
      }, RESIZEOBSERVERTIMEOUT + 10);
    },
  },
  methods: {
    getWidthHeight,
    getPlaceholderSource,
    getPreviewerCrop,
    getWidthAndHeight,
    init() {
      if (!this.observer) {
        // Create debounced functino

        this.replaceImgSrc = debounce(function (forceSize, force) {
          const sizes = [
            '100',
            '200',
            '250',
            '300',
            '400',
            '600',
            '725',
            '900',
            '1200',
            '1800',
            '2000',
          ];
          const devicePixelRatio =
            window.__CONVERDY_ENV__ === 'previewer'
              ? 1
              : window.devicePixelRatio || 1;
          const width = this.$el.offsetWidth * devicePixelRatio;

          //  Only replace when currentSize is smaller, or forced
          if (this.maxSizeLoaded > width && !force) {
            // console.log('no ned to swapt');
            return;
          }
          const width_to_use = forceSize
            ? forceSize
            : sizes.find((size) => size > width) || 2080;

          this.maxSizeLoaded = width_to_use;

          this.resizedSrc = `${CLOUDINARY_BASE}w_${width_to_use},f_auto/${
            this.properties.src.split(CLOUDINARY_BASE)[1]
          }`;
        }, RESIZEOBSERVERTIMEOUT);

        this.observer = new ResizeObserver((entries) => {
          for (let entry of entries) {
            this.replaceImgSrc();
          }
        });
      }

      this.observer.observe(this.$el);
      this.replaceImgSrc();
      return this.observer;
    },
    clean() {
      if (this.observer) {
        this.observer.unobserve(this.$el);
        this.observer = false;
      }
    },
  },
  mounted() {
    this.init();
  },
  destroyed() {
    this.clean();
  },
};
</script>

<style>
.BaseImage {
  width: 100%;
  max-width: 100%;
}
</style>
